import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Trophy, Sparkles, Star } from "lucide-react";
import { useGamification } from "../../contexts/GamificationContext";
import { useSounds } from "../../contexts/SoundsContext";
import { useLanguage } from "../../App";

const LevelUpModal = () => {
  const { showLevelUp, setShowLevelUp, level, totalXP, levelColor, newAchievements } = useGamification();
  const { playLevelUp, playAchievementUnlock } = useSounds();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [confetti, setConfetti] = useState([]);

  // Play sound effect when level up modal appears
  useEffect(() => {
    if (showLevelUp) {
      playLevelUp();
    }
  }, [showLevelUp, playLevelUp]);

  // Generate confetti particles
  useEffect(() => {
    if (showLevelUp) {
      const particles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        color: ["#F59E0B", "#EF4444", "#10B981", "#3B82F6", "#8B5CF6"][Math.floor(Math.random() * 5)]
      }));
      setConfetti(particles);
    }
  }, [showLevelUp]);

  if (!showLevelUp) return null;

  return (
    <Dialog open={showLevelUp} onOpenChange={setShowLevelUp}>
      <DialogContent className="sm:max-w-md text-center">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {isEnglish ? "🎉 Level Up!" : "🎉 Naik Level!"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Level Badge */}
          <div className="flex justify-center">
            <div
              className="relative"
              style={{
                animation: "bounce 1s infinite"
              }}
            >
              <div
                className={`
                  w-32 h-32 rounded-full flex items-center justify-center
                  bg-gradient-to-br from-yellow-400 to-orange-500
                  shadow-2xl
                `}
              >
                <div className="text-center text-white">
                  <div className="text-5xl font-bold mb-1">{level}</div>
                  <div className="text-sm font-medium">
                    {isEnglish ? "LEVEL" : "LEVEL"}
                  </div>
                </div>
              </div>

              {/* Sparkles */}
              <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-400 animate-spin-slow" />
              <Star className="absolute -top-4 -left-4 w-6 h-6 text-yellow-300 animate-pulse" />
              <Trophy className="absolute -bottom-2 right-0 w-7 h-7 text-orange-400 animate-bounce" />
            </div>
          </div>

          {/* Total XP */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {isEnglish ? "Total XP" : "Total XP"}
            </p>
            <p className="text-3xl font-bold" style={{ color: levelColor }}>
              {totalXP.toLocaleString()}
            </p>
          </div>

          {/* New Achievements */}
          {newAchievements && newAchievements.length > 0 && (
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm font-semibold mb-2">
                {isEnglish ? "New Achievements Unlocked!" : "Achievement Baru Dibuka!"}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {newAchievements.map((achievementId) => (
                  <Badge key={achievementId} variant="secondary" className="text-lg">
                    🏆
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Motivational Message */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {isEnglish
                ? "Amazing progress! Keep up the great work!"
                : "Kemajuan luar biasa! Terus semangat!"}
            </p>
          </div>

          {/* Action Button */}
          <Button
            className="w-full"
            onClick={() => setShowLevelUp(false)}
            style={{ backgroundColor: levelColor }}
          >
            {isEnglish ? "Continue" : "Lanjutkan"}
          </Button>
        </div>

        {/* Confetti Animation */}
        <style jsx>{`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin-slow 3s linear infinite;
          }
        `}</style>

        {/* Confetti Particles */}
        {confetti.map((particle) => (
          <div
            key={particle.id}
            className="absolute pointer-events-none"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animation: `fall ${particle.duration}s ease-in ${particle.delay}s forwards`,
              opacity: 0
            }}
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: particle.color }}
            />
          </div>
        ))}

        <style jsx>{`
          @keyframes fall {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg);
              opacity: 0;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default LevelUpModal;
